import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { getRequest, postRequest, deleteRequest, patchRequest } from "../helpers/fetchHandler"
import type { Team } from "../constants/types"

const teamKeys = {
  all: ["teams"] as const
}

export function useTeams() {
  return useQuery({
    queryKey: teamKeys.all,
    queryFn: () => getRequest({ url: "/api/v1/profile/teams", auth: true }) as Promise<Team[]>
  })
}

export function useCreateTeam() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string }) => postRequest({ url: "/api/v1/profile/teams", data, auth: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.all })
    }
  })
}

export function useInviteUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ teamId, data }: { teamId: string; data: Record<string, unknown> }) =>
      postRequest({ url: `/api/v1/profile/team/${teamId}/invite`, data, auth: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.all })
    }
  })
}

export function useRemoveMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ teamId, teamUserId }: { teamId: string; teamUserId: string }) =>
      deleteRequest({ url: `/api/v1/profile/team/${teamId}/remove/${teamUserId}`, auth: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.all })
    }
  })
}

export function useEditMember() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      teamId,
      teamUserId,
      data
    }: {
      teamId: string
      teamUserId: string
      data: Record<string, unknown>
    }) => patchRequest({ url: `/api/v1/profile/team/${teamId}/edit/${teamUserId}`, data, auth: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.all })
    }
  })
}
